import React, { useState, useEffect, useContext } from 'react';
import { string, bool } from 'prop-types';

import { extend, useDataModel, QueryProvider, QueryContext } from '@thd-nucleus/data-sources';
import { podFulFillmentUtils } from '@thd-olt-component-react/fulfillment';
import { ApplianceBundles } from '@thd-olt-component-react/appliance-bundles';
import { FrequentlyBoughtTogether } from '@thd-olt-component-react/frequently-bought-together';
import { useConfigService, ExperienceContext, useStoreId } from '@thd-nucleus/experience-context';

import { BundleMini } from './BundleMini';
import { useBundle } from '../hooks/useBundle';
import ZoneCard from '../subcomponents/zone-card';
import { ProductDataModel } from '../models/dataModel';
import { PackagesMiniAbTest } from '../subcomponents/PackagesMiniAbTest';
import { RENDER_TRANSACTIONAL, RENDER_VISUAL, RENDER_CO_WORK } from '../constants/index';
import { BundleMiniContextProvider } from '../context/BundleMiniContext';

const BundlePmvFbtLayoutMa = ({ itemId, showPackagesMini, name }) => {

  const storeId = useStoreId();
  const { data: productData } = useDataModel('clientOnlyProduct', {
    variables: {
      itemId,
      storeId
    },
    skip: !itemId,
    ssr: false
  });

  const ctx = useContext(ExperienceContext);
  const { defaultVariables } = useContext(QueryContext);
  const [packagesMiniABTestType, setPackagesMiniABTestType] = useState('default');
  const applianceBundlesRedesignEnabled = useConfigService('fs:applianceBundlesRedesignEnabled') || false;
  const enableNewPartsServicesDrawer = useConfigService('enableNewPartsServicesDrawer') || false;
  const isB2BCustomer = ctx?.customer?.type === 'b2b';
  const { bundleIds } = useBundle({ product: productData?.product });
  const [isPackageEnabled, setIsPackageEnabled] = useState(showPackagesMini);

  useEffect(() => {
    [RENDER_TRANSACTIONAL, RENDER_VISUAL, RENDER_CO_WORK].forEach((type) => {
      LIFE_CYCLE_EVENT_BUS.lifeCycle.on(`packages-mini.${type}`, (data) => {
        setPackagesMiniABTestType(data.output.enabled ? type : 'default');
      });
    });
  }, []);

  if (!productData) { return null; }

  if (bundleIds) {
    return (
      <BundleMiniContextProvider itemId={itemId}>
        <QueryProvider
          cacheKey="bundle-mini-provider"
          defaultVariables={defaultVariables.current}
        >
          <BundleMini bundleIds={bundleIds} name={name} />
        </QueryProvider>
      </BundleMiniContextProvider>
    );
  }

  if (isPackageEnabled) {
    return (
      <div data-testid={packagesMiniABTestType} className="sui-w-full">
        <PackagesMiniAbTest
          itemId={itemId}
          renderComponent={packagesMiniABTestType}
          callOnLoaded={({ didRender }) => {
            setIsPackageEnabled(didRender);
          }}
        />
      </div>
    );
  }

  return (
    <ZoneCard>
      {applianceBundlesRedesignEnabled && enableNewPartsServicesDrawer ? (
        <div data-testid="appliances-bundle">
          <ApplianceBundles itemId={itemId} isB2BCustomer={isB2BCustomer} />
        </div>
      ) : (
        <div data-testid="fbt">
          <FrequentlyBoughtTogether
            itemId={itemId}
            onLoadDynamicRecs={() => { }}
            hydrator
          />
        </div>
      )}
    </ZoneCard>
  );
};

BundlePmvFbtLayoutMa.propTypes = {
  itemId: string,
  showPackagesMini: bool,
  name: string
};

BundlePmvFbtLayoutMa.defaultProps = {
  itemId: '',
  showPackagesMini: true,
  name: ''
};

BundlePmvFbtLayoutMa.dataModel = extend(
  ProductDataModel,
  PackagesMiniAbTest,
  podFulFillmentUtils
);

BundlePmvFbtLayoutMa.displayName = 'BundlePmvFbtLayoutMa';

export { BundlePmvFbtLayoutMa };